import {
  useUpdateTransferRequest,
  useVerifyRejectDocuments,
} from "@features/cx/transfer/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { SubAccountCard } from "./cards/SubAccountCard";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { CardContent } from "@shared/ui/card";
import {
  CheckCircle,
  DollarSign,
  FileText,
  Paperclip,
  Settings,
  X,
  Users,
  Eye,
} from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TransferRequest } from "../../../types";
import { useUpdateConsumerDocument } from "@features/cx/transfer/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { TenantAccountCard } from "./cards/TenantCard";
import { formatTerritoryAddress } from "@shared/utils/address";

interface ClosureRequirements {
  customerConfirmation: boolean;
  qualityAssuranceDone: boolean;
}

interface TransferDetailsTabProps {
  request: TransferRequest;
  onResolve?: (requestId: string, payload: any) => void;
  onReject?: (requestId: string) => void;
  onRejectConfirm?: (reason: string) => void;
}

interface Requirement {
  label: string;
  status: boolean;
}

// Utility to format date as 'YYYY-MM-DD HH:mm:ss.SSS +0530' (IST)
function formatDateIST(date = new Date()) {
  const offset = 330; // IST offset in minutes
  const pad = (n, z = 2) => ("00" + n).slice(-z);
  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  const ist = new Date(utc + offset * 60000);
  const yyyy = ist.getFullYear();
  const mm = pad(ist.getMonth() + 1);
  const dd = pad(ist.getDate());
  const hh = pad(ist.getHours());
  const min = pad(ist.getMinutes());
  const ss = pad(ist.getSeconds());
  const ms = pad(ist.getMilliseconds(), 3);
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}.${ms} +0530`;
}

const TransferDetailsTab: React.FC<TransferDetailsTabProps> = ({
  request,
  onResolve,
  onReject,
}) => {
  console.log("Request1", request);
  const { id } = useParams<{ id: string }>();
  const { mutateAsync: updateServiceRequest } = useUpdateTransferRequest(
    Number(id),
  );

  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>(
    {},
  );

  const navigate = useNavigate();

  const { mutate: verifyRejectDocument } = useVerifyRejectDocuments(id);

  const handleVerify = (id: any) => {
    verifyRejectDocument({
      documentId: id,
      payload: { action: 1 },
    });
  };

  const handleReject = (id: any) => {
    verifyRejectDocument({
      documentId: id,
      payload: { action: 2 },
    });
  };

  useEffect(() => {
    if (request?.additionalData?.closureRequirements) {
      setCheckboxValues({
        "Proof of Identity":
          request.additionalData.closureRequirements.proofOfIdentity,
        "Proof of Address":
          request.additionalData.closureRequirements.proofOfAddress,
        "Transfer Authorization":
          request.additionalData.closureRequirements.transferAuthorization,
        "Financial Verification":
          request.additionalData.closureRequirements.financialVerification,
      });
    }
  }, [request]);
  const [documentStatuses, setDocumentStatuses] = useState<
    Record<number, number>
  >({});
  const currency = useCurrency();
  const handleResolveRequest = async () => {
    // Convert to snake_case
    const additionalData = Object.entries(checkboxValues).reduce(
      (acc, [label, isChecked]) => {
        const snakeCaseLabel = label.toLowerCase().replace(/\s+/g, "_");
        acc[snakeCaseLabel] = isChecked;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    const closeDate = formatDateIST();
    const payload = {
      status: 2,
      close_date: closeDate,
      additional_data: {
        closure_requirements: additionalData,
      },
    };
    updateServiceRequest(payload, {
      onSuccess: () => {
        logEvent("Transfer Approved", {
          id,
        });
      },
    });
    await queryClient.invalidateQueries({
      queryKey: QueryKeyFactory.module.cx.transferRequests.details(id),
    });
    navigate("/cx/transfer"); // Redirect after approve
  };

  const handleRejectRequest = async () => {
    if (onReject) {
      onReject(request.id.toString());
    }
    await queryClient.invalidateQueries({
      queryKey: QueryKeyFactory.module.cx.transferRequests.details(id),
    });
    navigate("/cx/transfer"); // Redirect after reject
  };

  console.log("Status Display", request.statusDisplay);

  const transferDetailsData = [
    { label: "Transfer ID", value: request?.requestNo ?? "NA" },
    {
      label: "Transfer Type",
      value: request?.utilitySupportRequest?.name ?? "NA",
    },
    {
      label: "Reason Code",
      value: request?.utilitySupportRequest?.configurationCode ?? "NA",
    },
    { label: "Priority", value: request?.priorityLevelDisplay ?? "NA" },
    {
      label: "Effective Date",
      value: request?.requestDate ?? "NA",
    },
  ];

  const slaTargets = [
    {
      label: "Acknowledgement Time",
      value:
        request?.utilitySupportRequest?.extraData?.acknowledgementTime ?? "-",
    },
    {
      label: "Response Time",
      value: request?.utilitySupportRequest?.extraData?.responseTime ?? "-",
    },
    {
      label: "Resolution Time",
      value: request?.utilitySupportRequest?.extraData?.resolutionTime ?? "-",
    },
  ];

  const slaAchieved = [
    {
      label: "Acknowledgement Time",
      value: request?.acknowledgementTimeAchieved ?? "Pending",
    },
    {
      label: "Response Time",
      value: request?.responseTimeAchieved ?? "Pending",
    },
    {
      label: "Resolution Time",
      value: request?.resolutionTimeAchieved ?? "Pending",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Transfer Details Card */}
      <div className="space-y-3">
        <h4 className="text-base font-semibold flex items-center gap-2 text-gray-900">
          <Settings className="h-4 w-4" />
          Transfer Details
        </h4>
        <div className="space-y-6 p-4 border border-gray-200 rounded-md bg-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {transferDetailsData.map((detail, index) => (
              <ValueLabel
                key={index}
                label={detail.label}
                value={detail.value}
              />
            ))}
          </div>

          <div className="pt-4 border-t border-gray-100">
            <ValueLabel
              label="SPECIAL INSTRUCTIONS"
              value={
                request?.additionalData?.specialInstructions ||
                "No special instructions provided."
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md">
          {/* SLA Target */}
          <div className="border border-gray-200 p-4 rounded-md">
            <p className="card-heading-text mb-4">SLA Target</p>
            <div className="grid grid-cols-3 w-full gap-4">
              {slaTargets.map((sla, index) => (
                <ValueLabel key={index} label={sla.label} value={sla.value} />
              ))}
            </div>
          </div>

          {/* SLA Achieved */}
          <div className="border border-gray-200 p-4 rounded-md">
            <p className="card-heading-text mb-4">SLA Achieved</p>
            <div className="grid grid-cols-3 w-full gap-4">
              {slaAchieved.map((sla, index) => (
                <ValueLabel
                  key={index}
                  label={sla.label}
                  value={sla.value}
                  className={sla.value !== "Pending" ? "text-green-600" : ""}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Conditional: Tenant Account Transfer Info (Transfer to Owner) */}
        {request?.additionalData?.transferTo === "owner" && (
          <div className="pt-6 border-t border-gray-100 mt-6">
            <TenantAccountCard
              owner={{
                name: request?.additionalData?.ownerAccountData?.name || "NA",
                number:
                  request?.additionalData?.ownerAccountData?.accountNo || "NA",
                phone: request?.additionalData?.ownerAccountData?.phone || "NA",
                address:
                  request?.additionalData?.ownerAccountData?.address || "NA",
              }}
            />
          </div>
        )}

        {/* Conditional: Sub-account Transfer Info (Transfer to Master) */}
        {request?.additionalData?.transferTo === "master" && (
          <div className="pt-6 border-t border-gray-100 mt-6">
            <SubAccountCard
              masterAccount={{
                name:
                  request?.consumer?.ownershipDetails?.approvalDetails
                    ?.masterAccountName || "NA",
                number:
                  request?.consumer?.ownershipDetails?.approvalDetails
                    ?.masterAccountNo || "NA",
                phone:
                  request?.consumer?.ownershipDetails?.approvalDetails
                    ?.masterAccountContactNumber || "NA",
                address:
                  formatTerritoryAddress({
                    service:
                      request?.consumer?.ownershipDetails?.approvalDetails
                        ?.masterAccountAddress,
                  }) || "NA",
              }}
            />
          </div>
        )}
      </div>
      {/* Transfer Details Section End */}

      <div>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* New Consumer Details */}
            {request?.additionalData?.transferTo !== "owner" &&
              request?.additionalData?.transferTo !== "master" && (
                <div className="space-y-3">
                  <h4 className="text-base font-semibold flex items-center gap-2 text-gray-900">
                    <Users className="h-4 w-4 text-blue-600" />
                    New Consumer Details
                  </h4>
                  <div className="p-4 border border-gray-200 rounded-md bg-white">
                    <div className="grid grid-cols-4 gap-x-6 gap-y-4">
                      <ValueLabel
                        label="NAME"
                        value={
                          `${request?.additionalData?.newConsumerData?.firstName || ""} ${request?.additionalData?.newConsumerData?.lastName || ""}`.trim() ||
                          "NA"
                        }
                      />
                      <ValueLabel
                        label="PHONE NUMBER"
                        value={
                          request?.additionalData?.newConsumerData
                            ?.contactNumber || "NA"
                        }
                      />
                      <ValueLabel
                        label="EMAIL"
                        value={
                          request?.additionalData?.newConsumerData?.email ||
                          "NA"
                        }
                      />
                      <ValueLabel
                        label="RELATIONSHIP"
                        value={request?.consumerRelationshipDisplay || "NA"}
                      />
                      <div className="col-span-2">
                        <ValueLabel
                          label="BILLING ADDRESS"
                          value={
                            request?.additionalData?.newConsumerData
                              ?.billingAddress || "NA"
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Verify Documents */}
            {request?.additionalData?.transferTo !== "owner" &&
              request?.additionalData?.transferTo !== "master" && (
                <div className="space-y-3">
                  <h4 className="text-base font-semibold flex items-center gap-2 text-gray-900">
                    <Paperclip className="h-4 w-4" />
                    Verify Documents
                  </h4>
                  <div className="p-4 border border-gray-200 rounded-md bg-white">
                    <div className="space-y-2">
                      {request?.additionalData?.newConsumerData?.document &&
                      request.additionalData.newConsumerData.document.length >
                        0 ? (
                        request.additionalData.newConsumerData.document.map(
                          (doc, idx) => {
                            const rawStatus = (doc?.statusDisplay)
                              .toString()
                              .toLowerCase();
                            const isPending = rawStatus === "pending";
                            const isVerified = rawStatus === "verified";
                            const isRejected = rawStatus === "rejected";

                            return (
                              <div
                                key={idx}
                                className="flex items-center justify-between bg-white rounded px-3 py-2"
                              >
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                  <div>
                                    <div className="font-medium text-sm">
                                      {doc.file?.split("/").pop() || "Document"}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {doc.document_type}
                                    </div>
                                    {(doc.reference_id || doc.referenceId) && (
                                      <div className="text-xs text-muted-foreground">
                                        Ref ID:{" "}
                                        {doc.reference_id || doc.referenceId}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {doc.file && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        window.open(
                                          doc.file,
                                          "_blank",
                                          "noopener,noreferrer",
                                        )
                                      }
                                    >
                                      <div className="flex items-center">
                                        <Eye className="h-4 w-4 mr-2" /> View
                                      </div>
                                    </Button>
                                  )}
                                  {isPending && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="success"
                                        onClick={() => handleVerify(doc.id)}
                                      >
                                        <div className="flex items-center">
                                          <CheckCircle className="h-4 w-4 mr-2" />{" "}
                                          Accept
                                        </div>
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => handleReject(doc.id)}
                                      >
                                        <div className="flex items-center">
                                          <X className="h-4 w-4 mr-2" /> Reject
                                        </div>
                                      </Button>
                                    </>
                                  )}
                                  {isVerified && (
                                    <Badge variant="success">Verified</Badge>
                                  )}
                                  {isRejected && (
                                    <Badge variant="destructive">
                                      Rejected
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          },
                        )
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          No documents uploaded
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Financial Summary */}
            <div className="space-y-3">
              <h4 className="text-base font-semibold flex items-center gap-2 text-gray-900">
                <DollarSign className="h-4 w-4" />
                Financial Summary
              </h4>
              <div className="p-4 border border-gray-200 rounded-md bg-white">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6">
                  <ValueLabel
                    label="TOTAL OUTSTANDING"
                    value={`${currency} ${request?.financialDetails?.outstandingBalance || "NA"}`}
                  />
                  <ValueLabel
                    label="NEXT BILL ON"
                    value={request?.financialDetails?.nextBillOn || "NA"}
                  />
                  <ValueLabel
                    label="LAST PAID AMOUNT"
                    value={`${currency} ${request?.financialDetails?.lastPayAmount || "NA"}`}
                  />
                  <ValueLabel
                    label="LAST PAID DATE"
                    value={request?.financialDetails?.lastPaymentDate || "NA"}
                  />
                  <div className="col-span-2 md:col-span-4">
                    <ValueLabel
                      label="FINANCIAL RESPONSIBILITY"
                      value={
                        request?.financialDetails?.financialResponsibility ||
                        "NA"
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Requirements Checklist */}
            {/* <div className="space-y-3">
              <h4 className="text-base font-semibold flex items-center gap-2 text-gray-900">
                <CheckSquare className="h-4 w-4" />
                Requirements Checklist
              </h4>
              <div className="p-4 border border-gray-200 rounded-md bg-white">
                <div className="space-y-2">
                  {requirements.map((req, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white border rounded px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={checkboxValues[req.label] || false}
                          onCheckedChange={(checked) => handleCheckboxChange(req.label, checked as boolean)}
                        />
                        <span className="text-sm">{req.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div> */}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              {(request.statusDisplay === "PENDING" ||
                request.statusDisplay === "ON HOLD" ||
                request.statusDisplay === "IN PROGRESS") && (
                <Button variant="destructive" onClick={handleRejectRequest}>
                  <X className="h-4 w-4 mr-2" />
                  Reject Request
                </Button>
              )}
              {(request.statusDisplay === "PENDING" ||
                request.statusDisplay === "ON HOLD" ||
                request.statusDisplay === "IN PROGRESS") && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleResolveRequest}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Transfer
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </div>
    </div>
  );
};

export default TransferDetailsTab;
